/**
 * RoomAccessPopup Component
 *
 * A popup dialog for joining rooms from the discovery map.
 * Allows users to:
 * - Enter room secret manually
 * - Enter PIN if required
 * - Request access from the room organizer
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import type { StoredRoom } from '@/lib/storage'
import type { GeoLocation } from '@/lib/types'
import { formatDistance } from '@/lib/geo'
import { formatDistanceToNow } from 'date-fns'

interface RoomAccessPopupProps {
  room: StoredRoom
  userLocation: GeoLocation | null
  onClose: () => void
  onRequestAccess?: (roomId: string, requesterName: string) => void
}

type AccessMode = 'options' | 'manual' | 'request'

export function RoomAccessPopup({
  room,
  userLocation,
  onClose,
  onRequestAccess,
}: RoomAccessPopupProps) {
  const navigate = useNavigate()
  const [accessMode, setAccessMode] = useState<AccessMode>('options')
  const [roomSecret, setRoomSecret] = useState('')
  const [pin, setPin] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [requestSent, setRequestSent] = useState(false)

  // Calculate distance to room
  let distanceText = ''
  if (userLocation) {
    const R = 6371000
    const dLat = (room.centerLat - userLocation.latitude) * Math.PI / 180
    const dLon = (room.centerLng - userLocation.longitude) * Math.PI / 180
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(room.centerLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c
    distanceText = formatDistance(distance)
  }

  const handleManualJoin = () => {
    if (!roomSecret.trim()) {
      setError('Room secret is required')
      return
    }

    // Store the secret and PIN in session storage for the join page to use
    sessionStorage.setItem(`room_${room.id}_secret`, roomSecret.trim())
    if (pin.trim()) {
      sessionStorage.setItem(`room_${room.id}_pin`, pin.trim())
    }

    navigate(`/room/${room.id}`)
  }

  const handleRequestAccess = async () => {
    if (!displayName.trim()) {
      setError('Display name is required')
      return
    }

    setIsRequesting(true)
    setError(null)

    try {
      // Call the request access callback if provided
      if (onRequestAccess) {
        onRequestAccess(room.id, displayName.trim())
      }

      // Store the display name for later use
      sessionStorage.setItem(`room_${room.id}_requester_name`, displayName.trim())

      setRequestSent(true)
    } catch {
      setError('Failed to send access request. Please try again.')
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-white/10 bg-black/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">📻</span>
              <h2 className="font-bold text-sm uppercase tracking-wider text-primary">
                {room.name}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white/80 transition-colors text-lg"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="mt-1 text-[10px] font-mono text-white/50">
            RANGE: {room.radiusMeters}m
            {distanceText && ` | D: ${distanceText}`}
          </div>
          <div className="text-[10px] text-white/30 uppercase mt-0.5">
            Active {formatDistanceToNow(room.createdAt, { addSuffix: true })}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {accessMode === 'options' && (
            <div className="space-y-3">
              <p className="text-xs text-white/60 mb-4">
                This room requires authentication. Choose how to join:
              </p>

              <button
                onClick={() => setAccessMode('manual')}
                className="w-full p-3 rounded-lg bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">🔐</span>
                  <div>
                    <div className="text-xs font-bold text-primary uppercase tracking-wider">
                      Enter Secret
                    </div>
                    <div className="text-[10px] text-white/50">
                      I have the room secret and/or PIN
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setAccessMode('request')}
                className="w-full p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">📨</span>
                  <div>
                    <div className="text-xs font-bold text-white uppercase tracking-wider">
                      Request Access
                    </div>
                    <div className="text-[10px] text-white/50">
                      Ask the organizer for permission to join
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {accessMode === 'manual' && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setAccessMode('options')
                  setError(null)
                }}
                className="text-xs text-white/40 hover:text-white/60 transition-colors font-mono"
              >
                ← Back
              </button>

              <div>
                <label className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                  Room Secret *
                </label>
                <input
                  type="password"
                  value={roomSecret}
                  onChange={(e) => {
                    setRoomSecret(e.target.value)
                    setError(null)
                  }}
                  placeholder="Paste room secret here"
                  className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm font-mono placeholder:text-white/30 focus:outline-none focus:border-primary/50"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-white/60 uppercase tracking-wider mb-2">
                  PIN (if required)
                </label>
                <input
                  type="password"
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value)
                    setError(null)
                  }}
                  placeholder="Optional PIN"
                  className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm font-mono placeholder:text-white/30 focus:outline-none focus:border-primary/50"
                  maxLength={6}
                />
              </div>

              {error && (
                <div className="p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                  {error}
                </div>
              )}

              <button
                onClick={handleManualJoin}
                className="w-full py-2.5 bg-primary text-white font-bold uppercase tracking-widest text-xs rounded-lg hover:bg-primary/90 transition-colors shadow-[0_0_15px_rgba(242,13,128,0.3)]"
              >
                UPLINK
              </button>
            </div>
          )}

          {accessMode === 'request' && !requestSent && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setAccessMode('options')
                  setError(null)
                }}
                className="text-xs text-white/40 hover:text-white/60 transition-colors font-mono"
              >
                ← Back
              </button>

              <p className="text-xs text-white/60">
                Send an access request to the room organizer. They will receive your request and can approve or deny it.
              </p>

              <div>
                <label className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                  Your Display Name *
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value)
                    setError(null)
                  }}
                  placeholder="Enter your name"
                  className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary/50"
                  maxLength={20}
                />
              </div>

              {error && (
                <div className="p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                  {error}
                </div>
              )}

              <button
                onClick={handleRequestAccess}
                disabled={isRequesting}
                className={clsx(
                  'w-full py-2.5 font-bold uppercase tracking-widest text-xs rounded-lg transition-colors',
                  isRequesting
                    ? 'bg-white/10 text-white/40 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary/90 shadow-[0_0_15px_rgba(242,13,128,0.3)]'
                )}
              >
                {isRequesting ? 'Sending...' : 'Request Access'}
              </button>
            </div>
          )}

          {accessMode === 'request' && requestSent && (
            <div className="space-y-4 text-center py-4">
              <div className="text-4xl mb-2">📬</div>
              <h3 className="font-bold text-primary uppercase tracking-wider text-sm">
                Request Sent
              </h3>
              <p className="text-xs text-white/60">
                Your access request has been sent to the room organizer. You'll be notified when they respond.
              </p>
              <div className="text-[10px] text-white/30 font-mono">
                Request ID: {room.id.slice(0, 8)}...
              </div>
              <button
                onClick={onClose}
                className="w-full py-2 bg-white/10 text-white font-bold uppercase tracking-widest text-xs rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>

        {/* Footer hint */}
        {accessMode === 'options' && (
          <div className="px-4 py-3 border-t border-white/5 bg-black/20">
            <p className="text-[9px] text-white/30 font-mono text-center">
              Room secrets are shared by organizers via QR code or secure message
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
